import { useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import logger from '../utils/logger'
import { sanitizeTrackingProperties, validateTrackingProperties } from '../utils/trackingPropertySanitizer'
import { WHATSAPP_TRACKING_CONFIG } from './whatsAppTrackingConstants'
import useTrackingSession from './useTrackingSession'

export default function usePendoWhatsAppTracking() {
  const location = useLocation()
  const { sessionId, startNewSession, extendSession, clearSession } = useTrackingSession()

  const trackWhatsAppEvent = useCallback(
    (eventName: string, properties: Record<string, any> = {}) => {
      const startTime = performance.now()

      try {
        // Validate inputs
        if (!eventName || typeof eventName !== 'string') {
          logger.warn('Invalid event name provided to WhatsApp tracking:', eventName)
          return
        }

        // Validate properties count and size
        validateTrackingProperties(properties, eventName)

        // Ensure we have the pendo object
        if (typeof window !== 'undefined' && window.pendo) {
          const fullEventName = eventName.startsWith('whatsapp_') ? eventName : `whatsapp_${eventName}`

          // Sanitize properties to prevent tracking failures
          const sanitizedProperties = sanitizeTrackingProperties(properties)

          // Add common properties
          const enhancedProperties = {
            ...sanitizedProperties,
            timestamp: new Date().toISOString(),
            current_url: location.pathname,
            session_id: sessionId
          }

          logger.debug(`Tracking WhatsApp event: ${fullEventName}`, enhancedProperties)

          // Extend session on activity
          extendSession()

          // Track with additional error boundary
          try {
            window.pendo.track(fullEventName, enhancedProperties)
          } catch (trackingError) {
            logger.error('Pendo tracking call failed:', trackingError, fullEventName)
            // Don't re-throw - tracking failures shouldn't break user flow
          }
        } else {
          logger.warn('Pendo not available for WhatsApp tracking:', eventName)
        }
      } catch (error) {
        logger.error('Error in WhatsApp tracking handler:', error, eventName, properties)
      } finally {
        // Performance monitoring
        const duration = performance.now() - startTime
        if (duration > WHATSAPP_TRACKING_CONFIG.PERFORMANCE.SLOW_TRACKING_THRESHOLD) {
          logger.warn(`Slow WhatsApp tracking detected: ${eventName} took ${duration.toFixed(2)}ms`)
        }
      }
    },
    [location.pathname, sessionId, extendSession]
  )

  const startSetupSession = useCallback(() => {
    const newSessionId = startNewSession()
    logger.debug('Started new WhatsApp setup session:', newSessionId)
    return newSessionId
  }, [startNewSession])

  const getSessionId = useCallback(() => {
    return sessionId
  }, [sessionId])

  return {
    trackWhatsAppEvent,
    startSetupSession,
    getSessionId,
    clearSession
  }
}

// Declare global pendo type for TypeScript
declare global {
  interface Window {
    pendo?: {
      track: (eventName: string, properties?: Record<string, any>) => void
      identify: (visitor: any, account?: any) => void
      initialize: (config: any) => void
    }
  }
}
